import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Chip,
} from "@mui/material";
import { Edit, Delete, ArrowBack } from "@mui/icons-material";
import { assetCategories } from "../../../../common/consts";
import { formatCurrencyN } from "../../../../common/utils_t";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useAssets } from "../../../../hooks/useAssets";
// import { useAssets } from "../../hooks/useAssets";
// import ConfirmationModal from "../../components/ConfirmationModal";
// import { formatCurrencyN } from "../../../../common/utils_t";
// import { assetCategories } from "../../utils/consts";

const AssetDetail: React.FC = () => {
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>(
    "/core-accounting/asset-register/:id"
  );
  const asset_id = match?.params?.id || "";
  const { asset, loading, fetchAsset, deleteAsset } = useAssets();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (asset_id) {
      fetchAsset(asset_id);
    }
  }, []);

  const getCategoryName = (categoryId: string | undefined): string => {
    if (!categoryId) return "N/A";
    const category = assetCategories.find(
      (cat) => cat.id === String(categoryId) || cat.id === categoryId
    );
    return category?.name || String(categoryId);
  };

  const handleDelete = async () => {
    if (asset_id) {
      await deleteAsset(asset_id);
      history.push("/core-accounting/asset-register");
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  if (loading && !asset) {
    return (
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Asset Detail</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <IonLoading isOpen={loading} message="Loading asset..." />
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (!asset) {
    return (
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Asset Detail</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="error">
                Asset not found
              </Typography>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => history.push("/core-accounting/asset-register")}
                sx={{ mt: 2 }}
              >
                Back to Asset Register
              </Button>
            </Paper>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Asset Detail</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()}>Back</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 3 }}>
            {/* Header Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {asset.name}
                </Typography>
                {asset.asset_id && (
                  <Typography variant="body2" color="text.secondary">
                    Asset ID: {asset.asset_id}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() =>
                    history.push(
                      `/core-accounting/asset-register/${asset_id}/edit`
                    )
                  }
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteClick}
                >
                  Delete
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Basic Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{asset.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  ID Tag
                </Typography>
                <Typography variant="body1">{asset.id_tag || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {asset.description || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body1">
                  {getCategoryName(asset.category_id)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Condition
                </Typography>
                <Chip
                  label={asset.asset_condition || "N/A"}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Location Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Location Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Department/Area
                </Typography>
                <Typography variant="body1">
                  {asset.dept_area || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Room
                </Typography>
                <Typography variant="body1">{asset.room || "N/A"}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Purchase Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Purchase Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Purchase Date
                </Typography>
                <Typography variant="body1">
                  {asset.purchase_date
                    ? new Date(asset.purchase_date).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Supplier
                </Typography>
                <Typography variant="body1">
                  {asset.supplier || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Warranty Expiration
                </Typography>
                <Typography variant="body1">
                  {asset.warranty_expiration
                    ? new Date(asset.warranty_expiration).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Life Span
                </Typography>
                <Typography variant="body1">
                  {asset.life_span ? `${asset.life_span} years` : "N/A"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Financial Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Financial Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Unit Value
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {formatCurrencyN(asset.unit_value)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Quantity
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {asset.quantity}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Value
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  {formatCurrencyN(asset.value)}
                </Typography>
              </Grid>
              {asset.depreciation_value !== undefined &&
                asset.depreciation_value !== null && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Depreciation Value
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrencyN(asset.depreciation_value)}
                    </Typography>
                  </Grid>
                )}
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Metadata */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Metadata
            </Typography>
            <Grid container spacing={2}>
              {asset.created_at && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(asset.created_at).toLocaleString()}
                  </Typography>
                </Grid>
              )}
              {asset.last_update && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(asset.last_update).toLocaleString()}
                  </Typography>
                </Grid>
              )}
              {asset.created_by && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">{asset.created_by}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>

        <ConfirmationModal
          isOpen={deleteModalOpen}
          header="Delete Asset"
          message="Are you sure you want to delete this asset? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={handleDeleteCancel}
        />

        <IonLoading isOpen={loading} message="Loading asset..." />
      </IonContent>
    </IonPage>
  );
};

export default AssetDetail;
